import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

function Section({ title, icon, children, delay = 0, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ animationDelay: `${delay}s`, animation: `fadeInUp 0.35s ease ${delay}s forwards`, opacity: 0, padding: 0, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700, color: '#C9A84C', display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon && <span style={{ fontSize: 16 }}>{icon}</span>}{title}
        </h3>
        <ChevronDown size={16} color="#C9A84C" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease', flexShrink: 0 }} />
      </button>
      <div style={{ maxHeight: open ? '800px' : '0px', overflow: 'hidden', transition: 'max-height 0.35s ease' }}>
        <div style={{ padding: '0 18px 18px' }}>
          <div style={{ height: 1, background: 'rgba(201,168,76,0.12)', marginBottom: 14 }} />
          {children}
        </div>
      </div>
    </div>
  );
}

export default function FamilyReadingPage() {
  const navigate  = useNavigate();
  const { memberId } = useParams();
  const location  = useLocation();
  const { lang }  = useLang();
  const tr        = t[lang];

  const member    = location.state?.member;
  const [reading, setReading]  = useState(null);
  const [loading, setLoading]  = useState(true);
  const [error,   setError]    = useState('');

  useEffect(() => {
    api.get(`/api/family/${memberId}/reading`)
      .then(d => { setReading(d); setLoading(false); })
      .catch(err => {
        if (err.status === 402) navigate('/paywall');
        else { setError(err.message || 'Failed to load reading.'); setLoading(false); }
      });
  }, [memberId, navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0F1E', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ fontSize: 32, color: '#C9A84C', marginBottom: 16 }}>✦</div>
        <p className="shimmer-text" style={{ fontSize: 15, fontFamily: "'Playfair Display', serif" }}>
          Jyoti is reading the stars for {member?.name || 'your family member'}…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0F1E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, padding: 24 }}>
        <p style={{ color: '#C0392B', fontSize: 14, textAlign: 'center' }}>{error}</p>
        <button className="btn-secondary" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const color = reading?.colorReading;
  const food  = reading?.foodReading;
  const routine = reading?.routineReading;
  const remedy  = reading?.remedyReading;
  const quote   = reading?.quoteReading;

  return (
    <div data-testid="family-reading-page" style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Header */}
      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', padding: 0 }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <p style={{ fontSize: 11, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>
            Today's Reading for
          </p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#F5F0E8', fontWeight: 700 }}>
            {member?.name || reading?.memberName || 'Family Member'}
          </h1>
          <p style={{ fontSize: 11, color: '#8A8580', marginTop: 2 }}>
            {member?.relationship} · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Jyoti's Note */}
        {reading?.jyotisNote && (
          <div style={{
            background: 'linear-gradient(135deg,rgba(201,168,76,0.1) 0%,rgba(201,168,76,0.03) 100%)',
            border: '1px solid rgba(201,168,76,0.25)', borderRadius: 16, padding: '18px',
            animation: 'fadeInUp 0.35s ease forwards',
          }}>
            <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8, fontWeight: 700 }}>
              ✦ {tr.jyotisNote}
            </p>
            <p style={{ fontSize: 14, color: '#F5F0E8', lineHeight: 1.7 }}>{reading.jyotisNote}</p>
          </div>
        )}

        {/* Color */}
        {color && (
          <Section title={tr.colorToday} icon="🎨" delay={0.05} defaultOpen={true}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: color.hexCode, boxShadow: `0 0 20px ${color.hexCode}55`, flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif" }}>{color.name}</p>
                <p style={{ fontSize: 12, color: '#8A8580', marginTop: 2 }}>{color.reason}</p>
              </div>
            </div>
            {color.clothingTip && <p style={{ fontSize: 13, color: '#C9A84C', fontStyle: 'italic' }}>✦ {color.clothingTip}</p>}
          </Section>
        )}

        {/* Food */}
        {food && (
          <Section title={tr.foodToday} icon="🌿" delay={0.1}>
            {food.eat?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 11, color: '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>{tr.eatThese}</p>
                {food.eat.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: i < food.eat.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2D6A4F', flexShrink: 0, marginTop: 5 }} />
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8' }}>{item.item}</p>
                      {item.reason && <p style={{ fontSize: 11, color: '#8A8580', marginTop: 1 }}>{item.reason}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {food.avoid?.length > 0 && (
              <div>
                <p style={{ fontSize: 11, color: '#C0392B', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>{tr.avoidThese}</p>
                {food.avoid.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#C0392B', flexShrink: 0, marginTop: 5 }} />
                    <p style={{ fontSize: 13, color: '#8A8580' }}>{item.item}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Routine */}
        {routine && (
          <Section title={tr.routineToday} icon="🌅" delay={0.15}>
            {[['morning', tr.morning, routine.morning], ['afternoon', tr.afternoon, routine.afternoon], ['evening', tr.evening, routine.evening]].map(([key, label, val]) => val && (
              <div key={key} style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 11, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 4 }}>{label}</p>
                <p style={{ fontSize: 13, color: '#F5F0E8', lineHeight: 1.6 }}>{val}</p>
              </div>
            ))}
          </Section>
        )}

        {/* Remedy */}
        {remedy && (
          <Section title={tr.todaysRemedy} icon="🪔" delay={0.2}>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#F5F0E8', marginBottom: 6 }}>{remedy.title}</p>
            <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.6, marginBottom: 8 }}>{remedy.description}</p>
            {remedy.timing && <p style={{ fontSize: 12, color: '#C9A84C' }}>⏰ {remedy.timing}</p>}
          </Section>
        )}

      </div>
    </div>
  );
}
